
import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Eye, EyeOff, Check, AlertCircle } from "lucide-react";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

export const ResetPasswordForm = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [hasValidToken, setHasValidToken] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check for reset token in URL or query parameters
  useEffect(() => {
    const checkForToken = async () => {
      try {
        console.log("Checking for reset token...");
        
        // Get session from Supabase
        const { data: { session } } = await supabase.auth.getSession();
        
        // Check for session directly first
        if (session) {
          console.log("Valid session found:", session);
          setHasValidToken(true);
          return;
        }
        
        // If no session, check URL parameters
        console.log("No existing session, checking URL parameters");
        
        // Check various possible token locations
        if (location.hash && (
          location.hash.includes("access_token=") || 
          location.hash.includes("type=recovery")
        )) {
          console.log("Valid recovery parameters found in hash");
          setHasValidToken(true);
          return;
        }
        
        // Check query parameters
        const type = searchParams.get('type');
        const tokenInQuery = searchParams.get('token') || searchParams.get('access_token');
        
        if ((type === 'recovery' || type === 'signup') && tokenInQuery) {
          console.log("Valid token found in query parameters");
          setHasValidToken(true);
          return;
        }
        
        // No valid token found
        console.error("No valid reset token found");
        setTokenError("Reset link is invalid or has expired. Please request a new password reset.");
        setShowErrorDialog(true);
        
      } catch (error) {
        console.error("Error checking token:", error);
        setTokenError("There was a problem processing your reset token. Please try again.");
        setShowErrorDialog(true);
      }
    };
    
    checkForToken();
  }, [location, searchParams]);

  const validatePassword = () => {
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }
    
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    
    setPasswordError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hasValidToken) {
      setShowErrorDialog(true);
      return;
    }
    
    if (!validatePassword()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Attempt to update the password using the token already in the URL/hash
      const { error } = await supabase.auth.updateUser({ 
        password 
      });
      
      if (error) throw error;

      toast({
        title: "Password updated!",
        description: "Your password has been reset successfully.",
        variant: "default",
      });
      
      // Redirect to login after successful password reset
      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } catch (error) {
      console.error("Error updating password:", error);
      
      // Check for specific errors
      const errorMessage = (error as Error).message.toLowerCase();
      
      if (errorMessage.includes("session") || errorMessage.includes("expired") || errorMessage.includes("invalid")) {
        setTokenError("Your reset link has expired or been used already. Please request a new password reset.");
        setShowErrorDialog(true);
      } else {
        toast({
          title: "Error",
          description: (error as Error).message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-2xl font-bold text-center">Reset Your Password</h2>
        <p className="text-center text-muted-foreground">
          Please enter a new password for your account.
        </p>
        
        <div className="space-y-4">
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          
          {passwordError && (
            <div className="bg-red-50 p-3 rounded-md flex items-start">
              <AlertCircle className="text-red-500 mt-0.5 mr-2 h-4 w-4 flex-shrink-0" />
              <p className="text-xs text-red-700">{passwordError}</p>
            </div>
          )}
          
          <div className="bg-blue-50 p-3 rounded-md flex items-start">
            <Check className="text-blue-500 mt-0.5 mr-2 h-4 w-4 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              Your password should be at least 8 characters long and include a mix of letters, numbers, and symbols for security.
            </p>
          </div>
        </div>
        
        <Button 
          type="submit" 
          className="w-full" 
          disabled={loading || !hasValidToken}
        >
          {loading ? "Updating..." : "Reset Password"}
        </Button>
      </form>
      
      <ResetPasswordDialog
        open={showErrorDialog}
        onOpenChange={setShowErrorDialog}
        errorMessage={tokenError}
      />
    </>
  );
};
